import { currentToolCtx } from './context';

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Authenticated API request. Pulls `{ apiUrl, apiToken }` from the
 * AsyncLocalStorage-backed ToolCtx — every tool handler runs inside a
 * `withToolCtx()` frame set up by the registry dispatcher.
 *
 * Throws on non-2xx with a prefixed message MCP clients can pattern-match
 * (`[unauthorized]`, `[timeout]`, `[http-N]`).
 */
export async function mcpApiRequest<T>(
  method: string,
  path: string,
  body?: Record<string, unknown>
): Promise<T> {
  const ctx = currentToolCtx();
  if (!ctx) {
    throw new Error('[no-context] mcpApiRequest called outside of withToolCtx()');
  }

  const url = `${ctx.apiUrl}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${ctx.apiToken}`,
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'TimeoutError') {
      throw new Error(`[timeout] upstream API did not respond within ${DEFAULT_TIMEOUT_MS}ms`);
    }
    throw new Error(`[network] ${err instanceof Error ? err.message : String(err)}`);
  }

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error('[unauthorized] Your session may have expired.');
    }
    const text = await res.text().catch(() => '');
    throw new Error(`[http-${res.status}] ${text || res.statusText}`);
  }

  return (await res.json()) as T;
}
